import React, { useState, useEffect, useCallback } from "react";
import api, { fmtFull } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  FileSpreadsheet, Upload, Camera, Loader2, Trash2, AlertTriangle,
  Download, Sparkles, ScanLine, Image as ImageIcon, CheckCircle2,
} from "lucide-react";

const MODULE_OPTIONS = [
  { value: "so_thu_chi", label: "Sổ Thu Chi", desc: "Ghi nhận thu chi hàng ngày", icon: "📒" },
  { value: "bang_ke_tien_mat", label: "Bảng Kê Tiền Mặt", desc: "Tiền mặt thu/chi theo ca", icon: "💵" },
  { value: "bang_ke_doanh_thu", label: "Doanh thu Pippy", desc: "Doanh thu theo khu vực/ngày (6 cột)", icon: "🍹" },
  { value: "doanh_thu_ngay", label: "Doanh thu ngày", desc: "Đối chiếu sổ kết ca vs KiotViet (17 cột)", icon: "📊" },
  { value: "bao_cao_kd", label: "Báo cáo KD", desc: "Báo cáo kinh doanh tổng hợp", icon: "📈" },
  { value: "luong", label: "Quản lý Lương", desc: "Bảng lương nhân viên theo tháng", icon: "👤" },
  { value: "chungtu", label: "Chứng từ đối chiếu", desc: "AI đọc hoá đơn, đối chiếu Sổ Thu Chi", icon: "🔍" },
];

export default function ImportUploadTab() {
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [undoing, setUndoing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPin, setResetPin] = useState("");
  const [resetting, setResetting] = useState(false);

  // File confirmation popup state
  const [pendingFile, setPendingFile] = useState(null); // {file, type: "excel"|"ocr"|"multi"|"chungtu", files: []}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100

  // OCR/batch state
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [batchStatus, setBatchStatus] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [chungtuResults, setChungtuResults] = useState(null);

  useEffect(() => { return () => { if (pollInterval) clearInterval(pollInterval); }; }, [pollInterval]);

  const fetchHistory = useCallback(async () => {
    try { const r = await api.get("/accounting/import-history"); setHistory(r.data.files || []); } catch {}
  }, []);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const pollBatch = (bid) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/accounting/batch/${bid}`);
        setBatchStatus(data);
        const pct = data.total_files > 0 ? Math.round((data.processed / data.total_files) * 100) : 0;
        setUploadProgress(pct);
        if (data.status === "completed") { clearInterval(interval); setPollInterval(null); setUploadProgress(100); toast.success(`Hoàn tất: ${data.processed}/${data.total_files} file`); fetchHistory(); }
      } catch {}
    }, 3000);
    setPollInterval(interval);
  };

  // ---- File select handlers (open popup instead of uploading) ----
  const onFileSelect = (e, type) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingFile({ file: files[0], files: Array.from(files), type });
    setSelectedModule("");
    setConfirmOpen(true);
    e.target.value = "";
  };

  // ---- Confirm & Upload ----
  const handleConfirmUpload = async () => {
    if (!selectedModule) { toast.error("Vui lòng chọn danh mục"); return; }
    setConfirmOpen(false);
    const { file, files, type } = pendingFile;
    const targetModule = selectedModule;
    const importType = targetModule === "luong" ? "luong" : "thu_chi";

    setUploadProgress(5);

    if (type === "excel") {
      setImporting(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("import_type", importType);
      formData.append("target_module", targetModule);
      try {
        setUploadProgress(30);
        const { data } = await api.post("/accounting/import-excel", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        setUploadProgress(100);
        toast.success(data.message + (data.sheets_processed?.length ? ` (${data.sheets_processed.join(", ")})` : ""));
        fetchHistory();
      } catch (err) { toast.error(err.response?.data?.detail || "Lỗi import"); }
      finally { setImporting(false); setTimeout(() => setUploadProgress(0), 2000); }

    } else if (type === "ocr") {
      setUploading(true); setResults([]); setBatchStatus(null);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("auto_save", "true");
      formData.append("target_module", targetModule);
      try {
        setUploadProgress(20);
        const { data } = await api.post("/accounting/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
        setUploadProgress(100);
        setResults([data]);
        toast.success(data.saved_count > 0 ? `AI đã lưu ${data.saved_count} giao dịch` : "AI đã đọc xong");
        fetchHistory();
      } catch (err) { toast.error(err.response?.data?.detail || "Lỗi upload"); }
      finally { setUploading(false); setTimeout(() => setUploadProgress(0), 2000); }

    } else if (type === "multi") {
      setUploading(true); setResults([]); setBatchStatus(null);
      const formData = new FormData();
      for (const f of files) formData.append("files", f);
      formData.append("auto_save", "true");
      formData.append("target_module", targetModule);
      try {
        setUploadProgress(10);
        const { data } = await api.post("/accounting/upload-images", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
        setBatchStatus({ status: "processing", total_files: data.total_files, processed: 0, results: [] });
        toast.success(data.message); pollBatch(data.batch_id);
      } catch (err) { toast.error(err.response?.data?.detail || "Lỗi upload"); }
      finally { setUploading(false); }

    } else if (type === "chungtu") {
      setUploading(true); setChungtuResults(null);
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append(`file${i}`, files[i]);
      try {
        setUploadProgress(15);
        const { data } = await api.post("/accounting/upload-chungtu", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        setUploadProgress(100);
        setChungtuResults(data);
        toast.success(data.message);
        fetchHistory();
      } catch (err) { toast.error(err.response?.data?.detail || "Lỗi upload chứng từ"); }
      finally { setUploading(false); setTimeout(() => setUploadProgress(0), 2000); }
    }

    setPendingFile(null);
  };

  const handleUndo = async (fileId, filename) => {
    if (!window.confirm(`Hoàn tác: Xoá tất cả dữ liệu đã import từ "${filename}"?`)) return;
    setUndoing(fileId);
    try { const { data } = await api.delete(`/accounting/import-undo/${fileId}`); toast.success(data.message); fetchHistory(); }
    catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setUndoing(null); }
  };

  const handleReset = async () => {
    if (resetPin !== "6789") { toast.error("Mã PIN không đúng"); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/accounting/reset-today", { pin: "6789" });
      toast.success(data.message); setResetOpen(false); setResetPin(""); fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi reset"); }
    finally { setResetting(false); }
  };

  const displayResults = batchStatus?.results?.length > 0 ? batchStatus.results : results;

  return (
    <div className="space-y-4 max-w-5xl" data-testid="acct-import-upload">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2"><Download className="w-4 h-4 text-lime-400" /> Nhập liệu</h2>
        <Button onClick={() => setResetOpen(true)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7 gap-1" data-testid="reset-today-btn">
          <Trash2 className="w-3 h-3" /> Reset hôm nay
        </Button>
      </div>

      {/* Upload Progress Bar */}
      {uploadProgress > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3" data-testid="upload-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-slate-400">{uploadProgress < 100 ? "Đang xử lý..." : "Hoàn tất!"}</span>
            <span className="text-[10px] text-lime-400 font-bold">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-[#0f172a] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-lime-500 to-emerald-400 rounded-full transition-all duration-500" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs text-slate-400">Chọn file để tải lên. Hệ thống sẽ yêu cầu xác nhận danh mục trước khi xử lý.</p>
          <Badge className="text-[8px] bg-lime-500/10 text-lime-400 border-lime-500/20 border shrink-0">Pass: Qnnt2019</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Import Excel */}
          <div className="bg-[#0f172a] border-2 border-dashed border-[#334155] rounded-xl p-4 text-center hover:border-lime-500/30 transition-colors">
            <FileSpreadsheet className="w-7 h-7 text-lime-400 mx-auto mb-1.5" />
            <p className="text-[10px] text-slate-400 mb-2">Import Excel</p>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold rounded-lg cursor-pointer text-[10px]" data-testid="import-excel-btn">
              {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Chọn file .xlsx
              <input type="file" accept=".xlsx,.xls" onChange={e => onFileSelect(e, "excel")} className="hidden" disabled={importing} />
            </label>
          </div>
          {/* OCR single */}
          <div className="bg-[#0f172a] border-2 border-dashed border-indigo-500/20 rounded-xl p-4 text-center hover:border-indigo-500/40 transition-colors">
            <Camera className="w-7 h-7 text-indigo-400 mx-auto mb-1.5" />
            <p className="text-[10px] text-slate-400 mb-2">Ảnh/PDF (AI OCR)</p>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold rounded-lg cursor-pointer text-[10px]" data-testid="upload-ocr-btn">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Chọn file
              <input type="file" accept="image/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" onChange={e => onFileSelect(e, "ocr")} className="hidden" disabled={uploading} />
            </label>
          </div>
          {/* Multi files */}
          <div className="bg-[#0f172a] border-2 border-dashed border-amber-500/20 rounded-xl p-4 text-center hover:border-amber-500/40 transition-colors">
            <FileSpreadsheet className="w-7 h-7 text-amber-400 mx-auto mb-1.5" />
            <p className="text-[10px] text-slate-400 mb-2">Nhiều file / ZIP</p>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold rounded-lg cursor-pointer text-[10px]" data-testid="upload-multi-btn">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Chọn nhiều file
              <input type="file" accept="image/*,.pdf,.xlsx,.zip,.rar" multiple onChange={e => onFileSelect(e, "multi")} className="hidden" disabled={uploading} />
            </label>
          </div>
          {/* Chứng từ */}
          <div className="bg-[#0f172a] border-2 border-dashed border-violet-500/20 rounded-xl p-4 text-center hover:border-violet-500/40 transition-colors">
            <ScanLine className="w-7 h-7 text-violet-400 mx-auto mb-1.5" />
            <p className="text-[10px] text-slate-400 mb-2">Chứng từ đối chiếu</p>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-500 hover:bg-violet-400 text-white font-semibold rounded-lg cursor-pointer text-[10px]" data-testid="upload-chungtu-btn">
              <Upload className="w-3 h-3" />
              Chọn file
              <input type="file" accept="image/*,.pdf,.zip,.rar,.png,.jpg,.jpeg,.webp,.bmp,.tiff,.heic" multiple onChange={e => onFileSelect(e, "chungtu")} className="hidden" />
            </label>
          </div>
        </div>
      </div>

      {/* OCR Results */}
      {displayResults.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 space-y-2">
          <h3 className="text-xs font-bold text-slate-200">Kết quả AI ({displayResults.length} file)</h3>
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {displayResults.map((r, i) => (
              <div key={i} className="bg-[#0f172a] border border-[#334155]/50 rounded-lg p-2 flex items-center justify-between">
                <div><span className="text-[9px] text-slate-300">{r.filename || `File ${i+1}`}</span>
                  {r.ocr_result?.summary && <span className="text-[9px] text-emerald-400 ml-2">Thu: {fmtFull(r.ocr_result.summary.total_thu || 0)}</span>}
                  {r.saved_count > 0 && <Badge className="text-[8px] bg-lime-500/20 text-lime-400 border-0 ml-1">{r.saved_count} dòng</Badge>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chứng từ Results */}
      {chungtuResults && (
        <div className="bg-[#1e293b] border border-violet-500/20 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-violet-400" /> Kết quả đối chiếu ({chungtuResults.total_pages} trang)
            </h3>
            <div className="flex items-center gap-2">
              <Badge className="text-[9px] bg-lime-500/15 text-lime-400 border-lime-500/20 border">{chungtuResults.matched} khớp</Badge>
              {chungtuResults.unmatched > 0 && <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/20 border">{chungtuResults.unmatched} chưa khớp</Badge>}
            </div>
          </div>
          {chungtuResults.matched_results?.length > 0 && (
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {chungtuResults.matched_results.map((r, i) => (
                <div key={i} className="bg-lime-500/5 border border-lime-500/15 rounded-lg p-2.5 flex items-center gap-3">
                  <Sparkles className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-300 truncate">{r.label}</p>
                  </div>
                  <p className="text-[10px] text-lime-400 font-bold font-mono">{(r.ocr_data?.amount || 0).toLocaleString()}đ</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Import History */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
        <h3 className="text-xs font-bold text-slate-200 mb-2">Lịch sử import</h3>
        {history.length === 0 ? <p className="text-[10px] text-slate-500">Chưa có file nào</p> : (
          <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
            {history.map(f => (
              <div key={f.id} className={`flex items-center justify-between p-2 rounded-lg border ${f.reverted ? "border-red-500/20 bg-red-500/5 opacity-50" : "border-[#334155]/50 bg-[#0f172a]"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileSpreadsheet className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-300 truncate">{f.filename}</p>
                    <p className="text-[8px] text-slate-600">{f.created_at?.slice(0, 16)} · {f.uploaded_by}</p>
                  </div>
                  <Badge className="text-[8px] bg-sky-500/10 text-sky-400 border-0 shrink-0">{f.import_type === "luong" ? "Lương" : f.target_module || "KD"}</Badge>
                </div>
                {!f.reverted && (
                  <Button onClick={() => handleUndo(f.id, f.filename)} disabled={undoing === f.id} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 h-6 text-[9px] px-2 shrink-0">
                    {undoing === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Hoàn tác"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== CONFIRM MODULE POPUP ===== */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-md" data-testid="confirm-module-dialog">
          <DialogHeader>
            <DialogTitle className="text-lime-400 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Xác nhận danh mục nhập liệu
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">
              File: <span className="text-slate-200 font-medium">{pendingFile?.file?.name || "..."}</span>
              {pendingFile?.files?.length > 1 && <span className="text-slate-500 ml-1">(+{pendingFile.files.length - 1} file khác)</span>}
            </p>
            <p className="text-xs text-slate-400">Chọn danh mục dữ liệu sẽ được đưa vào:</p>

            <div className="space-y-2">
              {MODULE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedModule === opt.value ? "border-lime-500 bg-lime-500/10" : "border-[#334155] hover:border-[#475569] bg-[#0f172a]"}`}
                  data-testid={`module-option-${opt.value}`}
                >
                  <input type="radio" name="module" value={opt.value} checked={selectedModule === opt.value} onChange={() => setSelectedModule(opt.value)} className="hidden" />
                  <span className="text-lg">{opt.icon}</span>
                  <div className="flex-1">
                    <p className={`text-xs font-semibold ${selectedModule === opt.value ? "text-lime-400" : "text-slate-200"}`}>{opt.label}</p>
                    <p className="text-[9px] text-slate-500">{opt.desc}</p>
                  </div>
                  {selectedModule === opt.value && <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0" />}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingFile(null); }} className="border-[#334155] text-slate-400 text-xs">
              Huỷ
            </Button>
            <Button onClick={handleConfirmUpload} disabled={!selectedModule} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold text-xs gap-1" data-testid="confirm-upload-btn">
              <Upload className="w-3.5 h-3.5" /> Xác nhận & Tải lên
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-xs">
          <DialogHeader><DialogTitle className="text-red-400 text-sm">Reset dữ liệu hôm nay</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Nhập mã PIN để xoá toàn bộ dữ liệu đã import hôm nay.</p>
          <Input type="password" value={resetPin} onChange={e => setResetPin(e.target.value)} placeholder="Mã PIN" className="bg-[#0f172a] border-[#334155] text-slate-200 mt-2" maxLength={4} />
          <Button onClick={handleReset} disabled={resetting} className="w-full mt-2 bg-red-500 hover:bg-red-400 text-white font-semibold text-xs">
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Xác nhận Reset
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
